import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useConfigurationClinicDetailAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationClinicDetailAPI";
import { useConfigurationClinicOperationalCapacityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationClinicOperationalCapacityAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import CounterField from "../../auth_routes/Configuration/EquipmentSetup/CounterField";
import { IClinicOnboardCapacityModalProps } from "../interface";

const CapacityModalContent = ({ counters, setCounters, clinicCapacityItem }) => {
  const handleIncrement = (id: string) => () => {
    setCounters((prevCounters) => {
      const updatedCounters = prevCounters.map((counter) =>
        counter.equipment_id === id ? { ...counter, count: counter.count + 1 } : counter,
      );
      return updatedCounters;
    });
  };

  const handleDecrement = (id: string) => () => {
    setCounters((prevCounters) => {
      const updatedCounters = prevCounters.map((counter) =>
        counter.equipment_id === id ? { ...counter, count: Math.max(counter.count - 1, 0) } : counter,
      );
      return updatedCounters;
    });
  };

  return (
    <div className="p-2 overflow-auto bg-white rounded-[20px] flex-col justify-start items-end gap-6 inline-flex">
      <div className="flex-col justify-center items-start gap-4 flex">
        {clinicCapacityItem.map((item) => (
          <div key={item.id} className="justify-start items-center gap-16 inline-flex">
            <div className="justify-start items-center gap-2.5 flex">
              <div className="text-black text-left w-[200px] text-xs font-normal">{item.title}</div>
            </div>
            <div className="h-9 justify-start items-center gap-4 flex">
              <CounterField
                count={
                  counters && Array.isArray(counters)
                    ? counters?.find((counter) => counter.equipment_id === item.id)?.count
                    : 0
                }
                onIncrement={handleIncrement(item.id)}
                onDecrement={handleDecrement(item.id)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ClinicOnboardCapacityModal: React.FC<IClinicOnboardCapacityModalProps> = () => {
  let clinicsFeched = false;
  const params = useParams();
  const { setModalData: setCounterState } = useModalActions("CLINIC_ONBOARD_CAPACITY");
  const { getRouteKey } = useRouter();
  const routeIdConfig = getRouteKey("CONFIGURATION", "id");
  const fetchCapacityList = useConfigurationClinicOperationalCapacityListAPI();
  const { routeState } = useRouteInfo(routeIdConfig);
  const { clinicOperationalCapacityList: capacityList } = routeState.state as IConfigurationListState;
  const clinicCapacityItem = useSelectMenuReducer(capacityList, "equipment_name", "id");
  const counterState = useModalState("CLINIC_ONBOARD_CAPACITY");
  const [counters, setCounters] =
    useState<{ equipment_id: string; equipment_name: string; count: number }[]>(counterState);
  const { clinicDetails, execute: fetchClinicDetail } = useConfigurationClinicDetailAPI();

  useEffect(() => {
    if (counterState?.length == 0) {
      fetchCapacityList();
    }
  }, [counterState]);

  useEffect(() => {
    if (params["clinic_id"] && !clinicsFeched) {
      const clinicId = params["clinic_id"];
      fetchClinicDetail(clinicId);
      clinicsFeched = true;
    }
  }, [params["clinic_id"]]);

  useEffect(() => {
    if (capacityList || clinicDetails?.equipment_data) {
      const initialCountersData = capacityList?.map((c) => {
        const data = clinicDetails?.equipment_data?.find((e) => e.id === c.id);
        return {
          equipment_id: c.id,
          equipment_name: c.equipment_name,
          count: data ? +data.equipment_count : 0,
        };
      });
      if (initialCountersData?.length > 0) {
        setCounters(initialCountersData);
        setCounterState(initialCountersData);
      }
    }
  }, [capacityList, clinicDetails]);

  return (
    <ModalPrompt
      modalId="CLINIC_ONBOARD_CAPACITY"
      showIcon={false}
      titleIcon=""
      dialogClassName="sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 h-[500px] overflow-scroll"
      button={{
        primaryButtonText: "Save",
        secondaryButtonText: "Cancel",
        primaryButtonClassname:
          "px-6 cursor-pointer py-[9px] bg-sky-900 hover:bg-sky-900 rounded-md border border-slate-400 border-opacity-20 justify-center items-center gap-2 flex",
        primaryCallback: () => {
          setCounterState(counters);
        },
        secondaryCallback: () => {
          setCounterState(counterState);
          setCounters(counterState);
        },
        shouldHideOnClick: true,
      }}
      title="Operational Capacity"
      content={
        <CapacityModalContent setCounters={setCounters} counters={counters} clinicCapacityItem={clinicCapacityItem} />
      }
    />
  );
};

export default ClinicOnboardCapacityModal;
